import { FormInstance } from 'antd';
import { checkEnvironmentForControllable } from 'constants/GlobalConfig';
import { isEqual, cloneDeep } from 'lodash';
import { Moment } from 'moment';

/**
 * フォームが初期時と比べて変更されるかを確認する。
 *
 * @param {*} formInitValue
 * @param {*} formChangeValue
 * @returns {boolean}
 */
export const checkFormChange = (formInitValue: any, formChangeValue: any) => {
    // クローンオブジェクトを作成する
    const formInitValues = cloneDeep(formInitValue);
    const formChangeValues = cloneDeep(formChangeValue);
    
    if (formChangeValues === null || formChangeValues === undefined) return true;
    if (formInitValues === null || formInitValues === undefined) return true;

    const initKeys = Object.keys(formInitValues);
    const changeKeys = Object.keys(formChangeValues);

    if (initKeys.length !== changeKeys.length) return true;

    initKeys.sort();
    changeKeys.sort();

    const makerArray = new Array(initKeys.length);

    for (let index = 0; index < initKeys.length; index++) {
        const initElement = formInitValues[initKeys[index]];
        const changeElement = formChangeValues[changeKeys[index]];

        switch (typeof initElement) {
            case 'object':
                if (Array.isArray(initElement)) {
                    makerArray[index] = compareArrays(
                        initElement,
                        changeElement,
                        initKeys[index]
                    );
                } else if (initElement === null) {
                    if (changeElement === '') {
                        makerArray[index] = false;
                    } else {
                        makerArray[index] = initElement !== changeElement;
                    }
                } else {
                    makerArray[index] = compareNodeHtml(
                        initElement,
                        changeElement,
                        initKeys[index]
                    )
                }
                break;
            case 'string':
            case 'number':
            case 'boolean':
            default:
                makerArray[index] = initElement !== changeElement;
                break;
        }
    }

    return makerArray.some((key) => key === true);
};

/**
 * 2つの配列を並び、比較する。
 *
 * @param {*} arrayInit
 * @param {*} arrayChange
 * @param {string} key
 * @returns {boolean}
 */
const compareArrays = (arrayInit: any[], arrayChange: any[], key: string) => {
    if (arrayInit && arrayChange) {
        switch (key) {
            case 'target_organizations':
            case 'target_organization':
            case 'targetOrganizations':
            case 'target_roles':
            case 'targetRoles':
            case 'target_school_classes':
            case 'targetSchoolClasses':
            case 'targetUsers':
            case 'target_users':
                arrayInit.sort((a: any, b: any) =>
                    a.uuid.localeCompare(b.uuid)
                );
                arrayChange.sort((a: any, b: any) =>
                    a.uuid.localeCompare(b.uuid)
                );
                break;
            case 'files':
                const firstItemChange: any =
                    arrayChange && arrayChange.length > 0
                        ? arrayChange[0]
                        : undefined;
                const firstItemInit: any =
                    arrayInit && arrayInit.length > 0
                        ? arrayInit[0]
                        : undefined;

                if (firstItemChange?.response && !firstItemInit?.response) {
                    const files = arrayChange.map((item: any) => item.response);

                    if (files) {
                        arrayInit.sort();
                        files.sort();
                        return !isEqual(arrayInit, files);
                    }
                }
                break;
            case 'markDetails':
                break;
            default:
                arrayInit.sort();
                arrayChange.sort();
                break;
        }
    }

    return !isEqual(arrayInit, arrayChange);
};

/**
 * ターゲットオブジェクトをuuidのみのオブジェクト型に形式する。
 * 
 * @param {*} target 
 * @param {*} targetMe 
 * @returns {object}
 */
export const convertValuesTarget = (target: any, targetMe: any = undefined) => {
    if (target === undefined) return null;
    if (targetMe) {
        return {
            target_roles: target.target_roles,
            target_school_classes: target.target_school_classes.map(
                (item: any) => ({ uuid: item.uuid })
            ),
            target_users: targetMe.map((item: any) => ({ uuid: item.uuid })),
        };
    }

    return {
        target_roles: target.target_roles,
        target_school_classes: target.target_school_classes.map(
            (item: any) => ({ uuid: item.uuid })
        ),
        target_users: target.target_users.map((item: any) => ({
            uuid: item.uuid,
        })),
    };
};

/**
 * フォームの入力が空かどうか確認する
 * @param {*} value
 * @returns {boolean} true: 空, false: 入力あり
 */
export const isEmptyInput = (value: any): boolean => {
    if (value === undefined) {
        return true;
    } else if (value === null) {
        return true;
    } else if (Array.isArray(value) && value.length === 0) {
        return true;
    } else if (typeof value === 'string' && value === '') {
        return true;
    }
    return false;
};

/**
 * hh:mmのフォーマットで時刻をStringに変換する。
 * 
 * @param {Moment} time 
 * @returns {string}
 */
export const convertTimeToString = (time: Moment) => {
    if (isEmptyInput(time)) {
        return '';
    }
    return `${time.hours()}:${time.minutes()}`
}

/**
 * 入力が入力可能最大桁数を超えるかをチェックする。
 *
 * @param {string} value
 * @param {number} limitCharacter
 * @returns
 */
const isLimitCharacter = (value: string, limitCharacter = 10000) => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = value;
    const textContent = tempElement.textContent || tempElement.innerText || '';

    if (textContent.length > limitCharacter) {
        return true;
    }
    return false;
};

/**
 * 入力可能最大桁数を超えて入力した時にメッセージを表示する処理
 *
 * @param {FormInstance<any>} form
 * @param {string[]} fieldsName
 * @param {number} limitCharacter
 * @returns
 */
export const handleCheckInputLimitCharacter = (
    form: FormInstance<any>,
    fieldsName: string[],
    limitCharacter = 10000
) => {
    let isError = false;
    const fields = form.getFieldsValue(fieldsName);
    fieldsName.forEach((item: string | number) => {
        if (isLimitCharacter(fields[item], limitCharacter)) {
            form.setFields([
                {
                    name: item,
                    errors: [`${limitCharacter}桁未満を入力してください`],
                },
            ]);
            isError = true;
        } else {
            form.setFields([
                {
                    name: item,
                    errors: [],
                },
            ]);
        }
    });
    return isError;
};

/**
 * 日付を「YYYY/MM/DD HH:mm」の形式で文字列に変換する。
 * 
 * @param {[Moment, Moment]} duration 
 * @returns {string}
 */
export const convertDateToString = (duration: [Moment, Moment]) => {
    if (isEmptyInput(duration)) {
        return ['', ''];
    }
    const startAt = duration[0];
    const endAt = duration[1];

    let strStartAt = '';
    let strEndAt = '';
    if (isEmptyInput(startAt)) {
        strStartAt = '1970/01/01 00:00';
    } else {
        strStartAt = startAt.format('YYYY/MM/DD HH:mm');
    }

    if (isEmptyInput(endAt)) {
        strEndAt = '2037/12/31 23:59';
    } else {
        strEndAt = endAt.format('YYYY/MM/DD HH:mm');
    }

    return [strStartAt, strEndAt];
};

/**
 * HTMLエンティティをデコードする
 * 
 * @param node 
 * @returns 
 */
export const decodeHtmlEntities = (node: any) => {
    if (typeof node === 'object') return node;
    if (typeof node === 'string') {
        node = node.replaceAll('&nbsp;', ' ')
    }

    const divElement = document.createElement('div');
    divElement.innerHTML = node;

    return divElement;
};


/**
 * 2つのHTMLノードの比較を実行する。
 * 
 * @param {any} initElement
 * @param {any} changeElement
 * @param {string} key
 * @returns {boolean}
 */
const compareNodeHtml = (initElement: any, changeElement: any, key: string) => {
    if (initElement && changeElement) {
        switch (key) {
            case 'content':
            case 'after_message':
            case 'explanation':
            case 'afterMessage':
                return !initElement.isEqualNode(changeElement);
            default:
                break;
        }
    }
    return checkFormChange(initElement, changeElement);
};

/**
 * 空（から）かどうかのチェックを行う。
 * 
 * @param {any} node 
 * @returns {boolean}
 */
export const checkNodeEmpty = (node: any) => {
    return !node || node.textContent.trim() === '';
};

/**
 * Froalaリッチテキスト内の画像srcを処理する。
 * /control/file/view または /admin/file/view → /file/view に変換する。
 * どのフィールド（content, explanation, afterMessage等）にも使用可能。
 * 
 * @param {string} htmlContent 
 * @returns {string}
 */
export const processFilePathsForSubmit = (htmlContent: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
        const src = img.getAttribute('src');
        if (src) {
            img.setAttribute('src', src.replace(/\/(control|admin)\/file\/view/, '/file/view'));
        }
    });
    return doc.body.innerHTML;
};

/**
 * Froalaリッチテキスト内の画像srcを処理する。
 * /file/view → /control/file/view または /admin/file/view に変換する。
 * どのフィールド（content, explanation, afterMessage等）にも使用可能。
 * 
 * @param {string} htmlContent 
 * @param {boolean} isControllable 
 * @returns {string}
 */
export const processFilePathsForDisplay = (htmlContent: string | null | undefined): string => {
    // 説明文機能の追加前に作成されたレコードは explanation が null で返る。
    // DOMParser.parseFromString は引数を文字列に強制変換するため、null をそのまま渡すと
    // 文字列 "null" としてパースされ画面に表示・保存されてしまう (LGT-4766)
    if (!htmlContent) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const images = doc.querySelectorAll('img');
    const fileView = checkEnvironmentForControllable() ? '/control/file/view' : '/admin/file/view';
    images.forEach((img) => {
        const src = img.getAttribute('src');
        if (src) {
            img.setAttribute('src', src.replace(/\/file\/view/, fileView));
        }
    });
    return doc.body.innerHTML;
};
